import classNames from 'classnames'
import plansData from '../../../data/plans.json'
import currencies from '../../../data/currencies.json'
import Badge from '../../core/badge'
import Table from '../../core/table'
import Tooltip from '../../core/tooltip'
import ContentLoader from '../../core/loader'
import ButtonLoader from '../../core/button/ButtonLoader'
import {ImgButton} from '../../core/button'
import Dropdown from '../../core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import {DateFormats, formatDate} from '../../../utils/date'
import {colors} from '../../../utils/colors'
import AmountHelper from '../../../utils/amount'
import TextHelper from '../../../utils/text'
import Customer from '../../../utils/customer'
import {FileIcon, HorizontalMoreIcon, InfoIcon} from '../../../icons'
import AddOnsList from './AddOnsList'

const BADGE_COLORS = {
    WARNING: 'warning',
    SUCCESS: 'success',
    DANGER: 'danger'
}
const getReadablePlanName = (plan) => {
    return plansData[plan]?.display_name || Object.values(plansData).find(p => p.key === plan)?.display_name
}

const getSubscriptionStatus = (subscription, subStatuses) => {
    if (subscription?.payment_pending) {
        return {type: subStatuses.PAYMENT_PENDING, color: BADGE_COLORS.WARNING}
    }

    if (subscription?.active && !subscription?.payment_failed && subscription?.can_be_reactivated) {
        return {type: subStatuses.CANCELED, color: BADGE_COLORS.WARNING}
    }

    if (subscription?.can_be_reactivated || subscription?.payment_pending) {
        return null
    }

    if (subscription?.active && !subscription?.payment_failed) {
        return {type: subStatuses.ACTIVE, color: BADGE_COLORS.SUCCESS}
    }

    if (subscription?.payment_failed) {
        return {type: subStatuses.PAYMENT_FAILED, color: BADGE_COLORS.WARNING}
    }

    return {type: subStatuses.INACTIVE, color: BADGE_COLORS.DANGER}
}
const StatusBadge = ({subscription, text, subStatuses}) => {
    const status = getSubscriptionStatus(subscription, subStatuses)

    if (!status) return null

    const showTooltip = status.type === subStatuses.PAYMENT_PENDING || status.type === subStatuses.CANCELED
    const tooltipContent = status.type === subStatuses.PAYMENT_PENDING
        ? text.payment_pending_info
        : text.canceled_info

    return (
        <>
            <Badge color={status.color}>{text[status.type]}</Badge>
            {showTooltip && (
                <Tooltip content={tooltipContent} trigger="mouseenter" placement="left">
                    <InfoIcon color={colors.colorBgFilter} />
                </Tooltip>
            )}
        </>
    )
}

const InvoiceLink = ({url, text}) => (
    <span className="min-width">
        <a className="show-invoice" href={url} target="_blank" rel="noopener noreferrer">
            <FileIcon className="table-icon" />
            <span>{text.show}</span>
        </a>
    </span>
)
const PaymentHistoryTable = ({payments, text, currencies}) => {
    const tableHeader = [
        {label: text.purpose, className: 'min-width-250'},
        {label: text.amount, className: 'min-width'},
        {label: text.paid_date, className: 'min-width'},
        {label: text.interval, className: 'min-width-150'},
        {label: text.invoice, className: 'min-width'}
    ]

    const tableRows = payments?.map(payment => [
        <span className="min-width-250 payment-desc">{payment?.description}</span>,
        <span className="min-width">
            {AmountHelper.normalizePrice(payment?.paid_amount, currencies[payment?.currency])}
        </span>,
        <span className="min-width">
            {formatDate(payment?.paid_at, DateFormats.DASH_YMD_TIME_SHORT)}
        </span>,
        <span className="min-width-150">
            {TextHelper.capitalizeFirstChar(payment?.plan_interval)}
        </span>,
        <InvoiceLink url={payment?.hosted_invoice_url} text={text} />
    ])

    return (
        <Table
            className="payments-table"
            tableHeader={tableHeader}
            data={payments}
            tbodyData={tableRows}
            selectedData={payments}
            hasCheckbox={false}
            isMultiCheckable={false}
        />
    )
}

const SubscriptionActions = ({
    subscription,
    text,
    isExpanded,
    processingId,
    onToggleDetails,
    onActivate,
    onUpdatePayment,
    onCancel
}) => {
    const canUpdatePayment = subscription?.active && subscription?.payment_failed && !subscription?.payment_pending

    return (
        <Dropdown
            menuButton={
                <ImgButton className="table-action-btn">
                    <HorizontalMoreIcon />
                </ImgButton>
            }
            position="top"
        >
            <MenuItem onClick={onToggleDetails}>
                <span>{isExpanded ? text.hide_details : text.view_details}</span>
            </MenuItem>

            <MenuItem
                onClick={() => onActivate(subscription?.subscription_id)}
                disabled={!subscription?.can_be_reactivated}
            >
                <span>
                    {processingId === subscription?.subscription_id && <ButtonLoader />}
                    {text.reactivate}
                </span>
            </MenuItem>

            <MenuItem
                onClick={onUpdatePayment}
                disabled={!canUpdatePayment}
            >
                <span>{text.update_payment}</span>
            </MenuItem>

            <MenuDivider />

            <MenuItem
                className="danger"
                onClick={() => onCancel(subscription?.subscription_id)}
                disabled={!subscription?.can_be_canceled}
            >
                <span>{text.cancel}</span>
            </MenuItem>
        </Dropdown>
    )
}

const SubscriptionItem = ({
    subscription,
    text,
    subStatuses,
    isExpanded,
    processingId,
    paymentData,
    isPaymentFetching,
    onToggleExpand,
    onActivate,
    onUpdatePayment,
    onCancel
}) => {
    const planName = `${getReadablePlanName(subscription?.plan)} ${text.plan}`
    const createdDate = `${text.created} ${formatDate(subscription?.created_at, DateFormats.US_LONG)} • ${subscription?.payment_provider}`

    return (
        <div className="subscription-item">
            <div className={classNames('subscription-item-header', {active: isExpanded})}>
                <div className="subscription-item-left">
                    <div className="subscription-item-left__top">
                        <span className="subscription-plan-name">{planName}</span>
                        <StatusBadge subscription={subscription} text={text} subStatuses={subStatuses}/>
                    </div>

                    <div className="subscription-item-left__bottom">
                        <span className="subscription-created-date">{createdDate}</span>
                    </div>
                </div>

                <div className="subscription-item-right">
                    <span className={classNames('subscription-action-button', {active: isExpanded})}>
                        {Customer.isRoot() && (
                            <SubscriptionActions
                                subscription={subscription}
                                text={text}
                                isExpanded={isExpanded}
                                processingId={processingId}
                                onToggleDetails={onToggleExpand}
                                onActivate={onActivate}
                                onUpdatePayment={onUpdatePayment}
                                onCancel={onCancel}
                            />
                        )}
                    </span>
                </div>
            </div>

            {isExpanded && (
                <div className="subscription-item-details">
                    {subscription?.add_ons && subscription.add_ons.length > 0 &&
                    <AddOnsList addOns={subscription.add_ons} text={text}/>}
                    {isPaymentFetching ? (
                        <ContentLoader width="48px" height="48px" />
                    ) : paymentData?.data?.subscription?.payments?.length > 0 ? (
                        <>
                            <span className="payments-header">
                                {text.payment_history} ({paymentData.data.subscription.payments.length})
                            </span>
                            <div className="payment-details">
                                <PaymentHistoryTable
                                    payments={paymentData.data.subscription.payments}
                                    text={text}
                                    currencies={currencies}
                                />
                            </div>
                        </>
                    ) : (
                        <span>{text.no_payments_found}</span>
                    )}
                </div>
            )}
        </div>
    )
}

export default SubscriptionItem
